import { useCallback, useEffect, useRef } from 'react';
import { toast } from 'sonner';
import { gateFeeApi } from '../services/gateFeeApi';
import { socketService } from '../services/socket';

/**
 * useGateFeeFlow
 *
 * The terminal's gate fee is paid on eModal, not here. Same three-step shape as
 * useFixPlateFlow:
 *   1. POST /gate-fees/session — backend resolves the task's order into the
 *      container number and trade direction eModal's fee search needs.
 *   2. Send EMODAL_GATE_FEE_START to the extension. It opens eModal's Fee
 *      Payment page, searches the container, adds the gate fee to the cart —
 *      and stops there.
 *   3. A human picks the card and clicks Complete Purchase. The extension reads
 *      the receipt and POSTs /gate-fees/complete; the backend files it, switches
 *      the invoice's Port Fee line on, and emits gate_fee_paid, which this hook
 *      turns into the success toast.
 *
 * The stop in step 2 is deliberate and is the whole safety story: nothing we
 * automate ever chooses a payment method or spends money. The operator does.
 */

const EXT_ID = import.meta.env.VITE_CHROME_EXT_ID || 'nniodjlikchoomnhdbcnlfncdcpodbib';

/**
 * 收据看门狗。
 *
 * 一趟付款有好几个结局根本不会回到这张页面上：柜子本来就不欠费、付款前被从
 * 购物车里删掉、金额读不出来、人直接把标签页关了。这些结局插件都只写进自己的
 * 日志，页面这边等不到 gate_fee_paid —— 那条转圈的提示就会一直挂着。
 *
 * 到点只收提示，会话继续听着：付款要人工输卡，超时之后回执照样会到，
 * 到时候逐只补报，不会变成「钱付了没人说」。
 */
const RECEIPT_WATCHDOG_MS = 5 * 60 * 1000;

/** 同时最多留几趟会话在听。超了就把最老的那趟收掉。 */
const MAX_SESSIONS = 5;

export interface UseGateFeeFlowOptions {
  onPaid?: (payload: { orderId: number; amount: number | null }) => void;
  onError?: (payload: { reason: string }) => void;
}

interface GateFeePaidEvent {
  orderId?: number;
  containerNumber?: string | null;
  amount?: number | null;
  feeEnabled?: boolean;
  warning?: string | null;
}

/** 一趟 eModal 会话 —— 单柜也是一趟，只是 remaining 里只有一只。 */
interface PendingSession {
  toastId: string;
  /** orderId → 柜号。回执到一只删一只，空了这趟就算完。 */
  remaining: Map<number, string>;
  total: number;
  paidCount: number;
  /** 需要人看一眼的那几只（码头没配价、后端带回的 warning）。 */
  notes: string[];
  timer: ReturnType<typeof setTimeout> | null;
  /** 看门狗到点后转圈提示已经收掉，之后晚到的回执逐只单报。 */
  silent: boolean;
}

/** 一只柜的回执该怎么报。单柜和批量共用这一份措辞。 */
function describePaid(data: GateFeePaidEvent): { level: 'success' | 'warning'; text: string } {
  const amount = typeof data.amount === 'number' ? `$${data.amount.toFixed(2)}` : '';
  if (data.warning) {
    // Recorded, but we would not touch the invoice — the operator has to
    // know that half of it did not happen.
    return { level: 'warning', text: data.warning };
  }
  if (data.feeEnabled) {
    return { level: 'success', text: `Gate fee paid ${amount} — Port Fee is on the invoice` };
  }
  return {
    level: 'warning',
    text: `Gate fee paid ${amount}, but this terminal has no Port Fee price configured, `
      + 'so nothing was added to the invoice.',
  };
}

function sendEmodalGateFeeStart(payload: {
  sessionId: string;
  url: string;
  orderId: number;
  containerNumber: string;
  /** 批量时这一趟覆盖的全部柜子；单柜时不传。 */
  containers?: Array<{ orderId: number; containerNumber: string }>;
  tradeType: 'IMPORT' | 'EXPORT';
  terminalCode: string | null;
  userJwt: string;
}): Promise<{ ok: boolean; error?: string }> {
  return new Promise((resolve) => {
    if (!window.chrome?.runtime?.sendMessage) {
      resolve({ ok: false, error: 'Chrome runtime unavailable' });
      return;
    }
    try {
      chrome.runtime.sendMessage(
        EXT_ID,
        { type: 'EMODAL_GATE_FEE_START', ...payload },
        (resp: { ok?: boolean; error?: string } | undefined) => {
          if (chrome.runtime.lastError) {
            resolve({ ok: false, error: chrome.runtime.lastError.message });
          } else {
            resolve({ ok: !!resp?.ok, error: resp?.error });
          }
        }
      );
    } catch (e) {
      resolve({ ok: false, error: (e as Error).message });
    }
  });
}

export function useGateFeeFlow(options: UseGateFeeFlowOptions = {}) {
  const { onPaid, onError } = options;
  const sessionsRef = useRef<PendingSession[]>([]);
  const onPaidRef = useRef(onPaid);
  const onErrorRef = useRef(onError);
  useEffect(() => { onPaidRef.current = onPaid; }, [onPaid]);
  useEffect(() => { onErrorRef.current = onError; }, [onError]);

  /** 收掉那条转圈的提示，并把这趟会话从监听列表里摘掉。 */
  const closeSession = useCallback((s: PendingSession) => {
    if (s.timer) clearTimeout(s.timer);
    s.timer = null;
    if (!s.silent) toast.dismiss(s.toastId);
    sessionsRef.current = sessionsRef.current.filter((x) => x !== s);
  }, []);

  /** 看门狗到点：只收提示，不摘会话。 */
  const muteSession = useCallback((s: PendingSession) => {
    if (s.timer) clearTimeout(s.timer);
    s.timer = null;
    s.silent = true;
    toast.dismiss(s.toastId);

    // 这一条留着，而且不能只把圈收掉了事：圈无声无息地消失，看上去就是
    // 「查完了、没事」。原话里那句「如果本来就不欠费，这条可以忽略」删掉 ——
    // 一句让人什么都不用做的话，只会把该读的那半句也一起稀释掉。
    const stuck = Array.from(s.remaining.values()).filter(Boolean);
    const list = stuck.slice(0, 4).join(', ') + (stuck.length > 4 ? ` and ${stuck.length - 4} more` : '');
    if (s.paidCount > 0) {
      toast.warning(`No receipt yet for ${stuck.length} container(s): ${list}. They post automatically once paid.`);
    } else {
      toast.warning(`eModal never sent a receipt — gate fee not looked up: ${list}`);
    }
  }, []);

  const openSession = useCallback((
    toastId: string,
    remaining: Map<number, string>
  ): PendingSession => {
    // 上一趟还没收尾就又点了一次 —— 留着听，但别无限堆。
    while (sessionsRef.current.length >= MAX_SESSIONS) {
      closeSession(sessionsRef.current[0]);
    }
    const s: PendingSession = {
      toastId,
      remaining,
      total: remaining.size,
      paidCount: 0,
      notes: [],
      timer: null,
      silent: false,
    };
    s.timer = setTimeout(() => muteSession(s), RECEIPT_WATCHDOG_MS);
    sessionsRef.current = [...sessionsRef.current, s];
    return s;
  }, [closeSession, muteSession]);

  useEffect(() => {
    const handle = (raw: unknown) => {
      const data = (raw || {}) as GateFeePaidEvent;
      if (!data.orderId) return;
      // 这只柜是哪一趟的。认柜不认「当前那一单」—— 批量一趟几十只，
      // 从前只认一单，于是整批的回执一条都对不上号，提示就永远转下去。
      const s = sessionsRef.current.find((x) => x.remaining.has(data.orderId as number));
      if (!s) return;

      const container = s.remaining.get(data.orderId) || data.containerNumber || '';
      s.remaining.delete(data.orderId);
      s.paidCount += 1;

      const said = describePaid(data);
      if (s.silent) {
        // 看门狗已经放弃过了（多半还报过一句「还没收到回执」）。这会儿回执
        // 到了，是在**推翻刚才那句话** —— 必须说出来，否则就成了「钱付了
        // 没人说」。这一条不受「成功不出声」管。
        if (said.level === 'success') toast.success(said.text);
        else toast.warning(said.text);
      } else if (s.total === 1) {
        // 单柜、圈还转着：收圈本身就是结果，付成了不出声，费用当场落在单子上。
        // 只有「要看一眼」的才报。
        if (said.level === 'warning') toast.warning(said.text);
      } else {
        if (said.level === 'warning') s.notes.push(container ? `${container}: ${said.text}` : said.text);
        const done = s.total - s.remaining.size;
        toast.loading(`Gate fees posted for ${done} of ${s.total} containers…`, { id: s.toastId });
      }

      onPaidRef.current?.({ orderId: data.orderId, amount: data.amount ?? null });

      if (s.remaining.size > 0) return;

      // 这一趟全回来了。
      const wasSilent = s.silent;
      closeSession(s);
      // 全入账了就不出声（那条进度提示已经被 closeSession 收掉）；
      // 只有「有几只要看一眼」才留在屏幕上。
      if (!wasSilent && s.total > 1 && s.notes.length > 0) {
        toast.warning(`${s.notes.length} of ${s.total} containers need a look — ${s.notes.join('; ')}`);
      }
    };
    socketService.on('gate_fee_paid', handle);
    return () => { socketService.off('gate_fee_paid', handle); };
  }, [closeSession]);

  // 页面走了就别留下转圈的提示。
  useEffect(() => () => {
    sessionsRef.current.forEach((s) => {
      if (s.timer) clearTimeout(s.timer);
      if (!s.silent) toast.dismiss(s.toastId);
    });
    sessionsRef.current = [];
  }, []);

  const fail = (error: string) => {
    toast.error(error);
    onErrorRef.current?.({ reason: error });
    return { ok: false, error };
  };

  // The backend's 400s carry the actual refusal ("this order has no container
  // number yet"); axios throws it away for "Request failed with status code
  // 400". Dig it back out — api.ts only unwraps `data` on success.
  const apiErrorMessage = (err: unknown, fallback: string): string => {
    const res = (err as { response?: { data?: { message?: string } } })?.response;
    return res?.data?.message || (err as Error)?.message || fallback;
  };

  /**
   * 批量：勾好的那几张任务一起送进同一个 eModal 会话。
   *
   * 和单柜走同一条路 —— 后端返回的 containers 数组原样交给插件，插件把柜号一只一只
   * 填进查费框（每只后面敲一下空格变成小方块），收据回来时按柜号逐只入账。
   */
  const startGateFeeBatch = useCallback(async (
    taskIds: number[]
  ): Promise<{ ok: boolean; error?: string }> => {
    const userJwt = localStorage.getItem('token');
    if (!userJwt) return fail('Not signed in');
    if (!window.chrome?.runtime?.sendMessage) {
      return fail('EAloom extension not available — install/enable the extension first');
    }

    let session;
    try {
      session = await gateFeeApi.createSession({ taskIds });
    } catch (err) {
      return fail(apiErrorMessage(err, 'Failed to start the gate fee lookup'));
    }

    const containers = session.containers || [];
    if (containers.length === 0) return fail('None of the selected tasks has a container number.');

    // 收据回来会一只柜推一条 gate_fee_paid，所以这趟要把**每一只**都记下来，
    // 全回来了才收提示。
    const toastId = `gate-fee-batch-${Date.now()}`;
    const pending = openSession(
      toastId,
      new Map(containers.map((c) => [Number(c.orderId), c.containerNumber]))
    );
    toast.loading(`Looking up gate fees for ${containers.length} containers on eModal…`, { id: toastId });

    const handoff = await sendEmodalGateFeeStart({
      sessionId: session.sessionId,
      url: session.url,
      orderId: session.orderId,
      containerNumber: session.containerNumber,
      containers,
      tradeType: session.tradeType,
      terminalCode: session.terminalCode,
      userJwt,
    });
    if (!handoff.ok) {
      closeSession(pending);
      return fail(handoff.error || 'Extension failed to open eModal');
    }
    return { ok: true };
  }, [openSession, closeSession]);

  const startGateFee = useCallback(async (
    params: { taskId?: number | string; orderId?: number }
  ): Promise<{ ok: boolean; error?: string }> => {
    const userJwt = localStorage.getItem('token');
    if (!userJwt) return fail('Not signed in');
    if (!window.chrome?.runtime?.sendMessage) {
      return fail('EAloom extension not available — install/enable the extension first');
    }

    let session;
    try {
      session = await gateFeeApi.createSession(params);
    } catch (err) {
      return fail(apiErrorMessage(err, 'Failed to start the gate fee lookup'));
    }

    // Paying twice for the same box is money we don't get back, and eModal will
    // happily take it. Say so before the tab opens; the operator can still go
    // ahead, because a second, genuinely-due fee does happen.
    if (session.alreadyPaid.length > 0) {
      const last = session.alreadyPaid[0];
      toast.warning(
        `Heads up: a gate fee of $${Number(last.amount).toFixed(2)} is already on file for `
        + `${session.containerNumber}. Check eModal before paying again.`
      );
    }

    const toastId = `gate-fee-${session.orderId}-${Date.now()}`;
    const pending = openSession(
      toastId,
      new Map([[Number(session.orderId), session.containerNumber]])
    );
    toast.loading(`Looking up the gate fee for ${session.containerNumber} on eModal…`, { id: toastId });

    const handoff = await sendEmodalGateFeeStart({
      sessionId: session.sessionId,
      url: session.url,
      orderId: session.orderId,
      containerNumber: session.containerNumber,
      tradeType: session.tradeType,
      terminalCode: session.terminalCode,
      userJwt,
    });
    if (!handoff.ok) {
      closeSession(pending);
      return fail(handoff.error || 'Extension failed to open eModal');
    }
    return { ok: true };
  }, [openSession, closeSession]);

  return { startGateFee, startGateFeeBatch };
}
